import { Component, ViewChild, Optional, Inject } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { layoutServices } from 'src/app/services/layout.service';
import {
    firstValueFrom,
    map,
    switchMap,
    catchError,
    of,
    Observable,
    timer,
    timeout,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { CardCustomComponent } from 'src/app/shared/card-custom/card-custom.component';
import { EmailEditorComponent } from 'src/app/shared/email-editor/email-editor.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

interface DialogData {
    layoutId?: number;
    isModal?: boolean;
}

@Component({
    selector: 'app-add-edit-layout',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MaterialModule,
        CardCustomComponent,
        EmailEditorComponent,
    ],
    templateUrl: './add-edit-layout.component.html',
    styleUrl: './add-edit-layout.component.scss',
})
export class AddEditLayoutComponent {
    @ViewChild('emailEditor') emailEditor: EmailEditorComponent;

    layoutForm!: FormGroup;
    isEditMode: boolean = false;
    layoutId: boolean | number = false;
    isModal: boolean = false;

    constructor(
        private fb: FormBuilder,
        private layoutService: layoutServices,
        public router: Router,
        private route: ActivatedRoute,
        @Optional() private dialogRef: MatDialogRef<AddEditLayoutComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData
    ) {
        // Check if component is opened as modal
        this.isModal = this.data?.isModal || false;

        this.layoutForm = this.fb.group({
            name: [
                '',
                {
                    validators: Validators.required,
                    asyncValidators: this.checkNameValidator.bind(this),
                    updateOn: 'blur',
                },
            ]
        });
    }

    ngOnInit(): void {
        // Check if in modal mode first
        if (this.isModal && this.data?.layoutId) {
            this.layoutId = this.data.layoutId;
            this.isEditMode = true;
            this.getLayoutById(this.layoutId);
        } else if (this.isModal) {
            // New layout in modal mode
            this.isEditMode = false;
        } else {
            // Non-modal mode - use route params
            const duplicateId = this.route.snapshot.queryParamMap.get('duplicate');
            if (duplicateId) {
                this.getLayoutById(+duplicateId);
            } else {
                this.route.params.subscribe((params) => {
                    if (params['id']) {
                        this.layoutId = params['id'];
                        this.isEditMode = true;
                        this.getLayoutById(this.layoutId);
                    } else {
                        this.isEditMode = false;
                    }
                });
            }
        }
    }

    ngAfterViewInit() {
        if (this.emailEditor && !this.isEditMode) {
            this.emailEditor.loadTemplate("");
        }
    }

    async getLayoutById(layoutId: number | boolean) {
        const response = await firstValueFrom(
            this.layoutService.getOne(layoutId)
        );

        this.emailEditor.loadTemplate(response.template);
        this.layoutForm.patchValue({
            name: response.name,
        });
    }

    async onSubmit() {
        if (this.layoutForm.valid) {
            const formData = new FormData();
            const file = this.emailEditor.getTemplate();
            formData.append('data', JSON.stringify(this.layoutForm.value));
            formData.append('file', file || '[]');

            const response = await (this.isEditMode
                ? firstValueFrom(this.layoutService.put(formData, this.layoutId))
                : firstValueFrom(this.layoutService.post(formData)));

            if (this.isModal && this.dialogRef) {
                // Close dialog with success result
                this.dialogRef.close({ id: response.data.id, action: this.isEditMode ? 'updated' : 'created' });
            } else {
                // Normal navigation for non-modal
                this.router.navigate(['/admissions/layouts']);
            }
        }
    }

    checkNameValidator(
        control: AbstractControl
    ): Observable<ValidationErrors | null> {
        return timer(300).pipe(
            switchMap(() =>
                this.layoutService
                    .checkNameExists(control.value, this.layoutId)
                    .pipe(
                        timeout(2000),
                        map((exists) =>
                            exists ? { nameNotUnique: true } : null
                        ),
                        catchError(() => of(null))
                    )
            )
        );
    }

    cancel(): void {
        if (this.isModal && this.dialogRef) {
            // Close dialog without result
            this.dialogRef.close();
        } else {
            // Normal navigation for non-modal
            this.router.navigateByUrl('admissions/layouts');
        }
    }
}